import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Dialog } from "../design/Dialog";
import { Ankreuzfeld, Auswahl, Klappliste, Textfeld } from "../design/Formular";
import { Handlungen } from "../design/Handlungen";
import { Abweisung, Status } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { TextMitKennungen } from "../design/Kennung";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { dateiHerunterladen, istLeitung, KEIN_WERT, symbolDerAnsprache, useEigeneLage, useStrecke, wert, type Rahmen } from "./daten";

/**
 * Ansicht „Strecke“ (oberflaeche.md §3.2, §6; design/README Ansicht `#s-strecke`): Streckensumme je Wildart, je Eintrag ein
 * `article` mit Herkunft, Revier, Ansprache, „Erlegt durch“, Datum, Trichinenprobennummer und Wildmarke samt Anpassungen;
 * für die Leitung „Korrigieren“, „Entfernen“, „Wildmarke“, „Hinzufügen“, „Abschließen“, „Exportieren“ und der Weg zur
 * Jagddokumentation; für Mitwirkende „Rückmeldung“. Ob eine Handlung zulässig ist, entscheidet der Server (ADR-0010).
 */

type Offen =
  | { art: "Korrigieren" | "Entfernen" | "Wildmarke"; eintrag: Zeile }
  | { art: "Streckeneintrag" | "Abschließen" | "Exportieren" | "Rückmeldung" }
  | null;

/**
 * Überschrift eines Streckeneintrags (oberflaeche.md §1.5, V-21): sichtbar immer `<Ansprache> <Erlegt durch>`, damit jedes
 * Stück seinen Erleger in der Überschrift zeigt (Bericht B3 F6). Der Name des `article` bleibt der Titel des Vertrags:
 * `<Ansprache>`, nur bei Mehrdeutigkeit `<Ansprache> <Erlegt durch>`. Ein Eintrag ohne Erleger zeigt nur die Ansprache.
 */
export function ueberschriftDer(eintraege: readonly Zeile[], e: Zeile): { ansprache: string; erleger: string | null; mitErleger: boolean } {
  const ansprache = String(e["Ansprache"]);
  const erleger = e["Erlegt durch"] ? wert(e["Erlegt durch"]) : null;
  const gleich = eintraege.filter((x) => x["Ansprache"] === ansprache).length > 1;
  return { ansprache, erleger: erleger === KEIN_WERT ? null : erleger, mitErleger: gleich };
}

function Streckensumme({ stand, zusammengefasst }: { stand: Zeile | null; zusammengefasst: readonly Zeile[] }) {
  const wildarten = new Map<string, Zeile[]>();
  for (const z of zusammengefasst) wildarten.set(String(z["Wildart"]), [...(wildarten.get(String(z["Wildart"])) ?? []), z]);
  const abgeschlossen = stand?.["Streckenstatus"] === "abgeschlossen";
  return (
    <section className="liste-bereich" role="region" aria-label="Streckensumme">
      <div className="summe platte">
        <span className="summe__z">{String(stand?.["Stück"] ?? 0)}</span>
        <span>Stück</span>
        <span className={abgeschlossen ? "marke-status marke-status--stark" : "marke-status"}>{abgeschlossen ? <Symbol name="haken" /> : null}{wert(stand?.["Streckenstatus"] ?? "vorläufig")}</span>
      </div>
      {[...wildarten.entries()].map(([wildart, zeilen]) => (
        <div key={wildart} className="zeile zeile--wild platte">
          <Symbol name={symbolDerAnsprache(wildart)} />
          <span className="zeile__t">{wildart}</span>
          {zeilen.some((z) => z["Ansprache"] !== wildart)
            ? <span className="zeile__u">{zeilen.map((z) => `${String(z["Stück"])} ${String(z["Ansprache"]).slice(wildart.length + 1)}`).join(" · ")}</span> : null}
          <span className="zeile__z">{zeilen.reduce((s, z) => s + Number(z["Stück"] ?? 0), 0)}</span>
        </div>
      ))}
    </section>
  );
}

/**
 * Ein Streckeneintrag als `article` (oberflaeche.md §1.5): Überschrift, Angaben, für die Leitung die Handlungen. Kompakt
 * (Anforderungen K99 (3), `oberflaeche/11`): Die Angaben fließen als Paare aus Bezeichnung und Wert, die drei Handlungen
 * stehen in einer Reihe, damit ein Eintrag auf jedem Telefon der Gerätematrix ganz in den sichtbaren Bereich der Liste
 * passt; „Erlegt durch“ steht in der Überschrift (V-21) und nur dort, wo es keinen Erleger in der Überschrift gibt;
 * Trichinenprobennummer und Wildmarke erscheinen, sobald sie erfasst sind.
 */
export function Streckeneintrag({ e, titel, leitung, onHandlung }: { e: Zeile; titel: ReturnType<typeof ueberschriftDer>; leitung: boolean; onHandlung: (art: "Korrigieren" | "Entfernen" | "Wildmarke") => void }) {
  const id = `eintrag-${e._id}`;
  const name = titel.mitErleger && titel.erleger ? `${id}-a ${id}-e` : `${id}-a`;
  const angepasst = e["angepasst am"] !== null && e["angepasst am"] !== undefined;
  const zeilen: Array<[string, unknown]> = [
    ["Herkunft", e["Herkunft"]], ["Revier", e["Revier"]], ...(titel.erleger ? [] : [["Erlegt durch", e["Erlegt durch"]] as [string, unknown]]),
    ["Datum", e["Datum"]],
    // Trichinenprobennummer und Wildmarke erst, wenn erfasst („Wildmarke“ erfasst sie): ein Eintrag passt so auch in die
    // Liste von 375 × 548 (iPhone SE in Safari, Gerätetest G1)
    ...([["Trichinenprobennummer", e["Trichinenprobennummer"]], ["Wildmarke", e["Wildmarke"]]] as Array<[string, unknown]>).filter(([, v]) => v),
    // eine Korrektur zeigt die bisherige Ansprache und ihren Grund; wer und wann angepasst hat, steht in der
    // Jagddokumentation (kompakt, K99 (3))
    ...(angepasst && e["Ansprache bisher"] ? ([["Ansprache bisher", e["Ansprache bisher"]], ["Grund", e["Grund"]]] as Array<[string, unknown]>) : []),
  ];
  return (
    <article className="eintrag platte" aria-labelledby={name}>
      <Symbol name={symbolDerAnsprache(String(e["Ansprache"]))} />
      <h3 className="eintrag__t" id={id}>
        <span id={`${id}-a`}>{titel.ansprache}</span>
        {titel.erleger ? <>{" "}<span className="eintrag__erleger" id={`${id}-e`}>{titel.erleger}</span></> : null}
      </h3>
      <dl className="angaben angaben--fliessend">
        {zeilen.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{k === "Herkunft" ? <TextMitKennungen text={wert(v)} /> : wert(v)}</dd></div>)}
      </dl>
      {leitung ? (
        <div className="raster raster--knoepfe raster--reihe">
          <Kachel art="kompakt" symbol="korrigieren" begriff="Korrigieren" onPress={() => onHandlung("Korrigieren")} />
          <Kachel art="kompakt" symbol="schliessen" begriff="Entfernen" onPress={() => onHandlung("Entfernen")} />
          <Kachel art="kompakt" symbol="wildmarke" begriff="Wildmarke" onPress={() => onHandlung("Wildmarke")} />
        </div>
      ) : null}
    </article>
  );
}

export function Strecke({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { revier, rollen } = useEigeneLage(id);
  const { eintraege, stand, zusammengefasst, laedt } = useStrecke(id);
  const mitwirkende = useSammlung(revier ? `drueckjagden/${id}/reviere/${revier}/mitwirkende` : null).daten;
  const leitung = istLeitung(rollen);
  const mitwirkend = rollen.some((r) => r !== "Jagdherr");
  const [offen, setOffen] = useState<Offen>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [felder, setFelder] = useState<Record<string, string>>({});
  const [anonym, setAnonym] = useState(false);
  const feld = (k: string) => felder[k] ?? "";
  const setzen = (k: string) => (v: string) => setFelder((f) => ({ ...f, [k]: v }));

  function oeffnen(o: Offen, vorbelegt: Record<string, string> = {}) {
    setGrund(null);
    setHinweis(null);
    setFelder(vorbelegt);
    setAnonym(false);
    setOffen(o);
  }

  async function senden(pfad: string, daten: Record<string, unknown>): Promise<boolean> {
    const ohneLeere = Object.fromEntries(Object.entries(daten).filter(([, v]) => v !== "" && v !== undefined && v !== null));
    const a = await befehl(pfad, { "Drückjagd": id, ...ohneLeere });
    setOffen(null);
    if (!a.ok) setGrund(abweisungsgrund(a));
    return a.ok;
  }

  async function exportieren() {
    const a = await befehl("StreckenlisteExportieren", { "Drückjagd": id, Revier: revier, Format: feld("Format") || "PDF" });
    setOffen(null);
    const datei = (a.felder["datei"] as { url?: string } | undefined)?.url;
    const fehler = a.ok && datei ? await dateiHerunterladen(datei) : (a.grund ?? "unzulaessige-angaben");
    if (fehler) setGrund(fehler);
  }

  // Handlungen der Ansicht (oberflaeche.md §1.6): „Hinzufügen“ zuerst – sie bleibt auch zusammengefasst direkt sichtbar
  // (Anforderungen K101 (4)); ab drei Handlungen in drei Spalten, damit über ihnen mehr als ein Streckeneintrag sichtbar bleibt
  const unten = (
    <Handlungen handlungen={[
      ...(leitung ? [{ symbol: "hinzufuegen", begriff: "Hinzufügen", onPress: () => oeffnen({ art: "Streckeneintrag" }) }] : []),
      ...(leitung ? [{ symbol: "dokument", begriff: "Jagddokumentation", fuge: true, onPress: () => navigate(`/drueckjagd/${id}/jagddokumentation`) }] : []),
      ...(leitung ? [{ symbol: "herunterladen", begriff: "Exportieren", onPress: () => oeffnen({ art: "Exportieren" }, { Format: "PDF" }) }] : []),
      ...(rollen.includes("Jagdherr") ? [{ symbol: "haken", begriff: "Abschließen", onPress: () => oeffnen({ art: "Abschließen" }) }] : []),
      ...(mitwirkend ? [{ symbol: "nachricht", begriff: "Rückmeldung", onPress: () => oeffnen({ art: "Rückmeldung" }, { Bewertung: "" }) }] : []),
    ]} />
  );

  const eintrag = offen && "eintrag" in offen ? offen.eintrag : null;
  const eintragId = eintrag ? String((eintrag["_kennungen"] as Record<string, string> | undefined)?.["Schuss"] ?? eintrag._id) : null;
  return rahmen({
    drueckjagdId: id, name: "Strecke", unten,
    children: (
      <>
        <Abweisung grund={grund} />
        {hinweis ? <Status symbol="haken">{hinweis}</Status> : null}
        <Streckensumme stand={stand} zusammengefasst={zusammengefasst} />
        <section className="liste-bereich" role="region" aria-label="Stücke">
          <h2 className="kopf platte">Stücke</h2>
          {eintraege.length === 0 && !laedt ? <div className="leer"><Symbol name="strecke" /><span>{KEIN_WERT}</span></div> : null}
          {eintraege.map((e) => (
            <Streckeneintrag key={e._id} e={e} titel={ueberschriftDer(eintraege, e)} leitung={leitung}
              onHandlung={(art) => oeffnen({ art, eintrag: e }, art === "Korrigieren" ? { Ansprache: String(e["Ansprache"]) }
                : art === "Wildmarke" ? { Trichinenprobennummer: String(e["Trichinenprobennummer"] ?? ""), Wildmarke: String(e["Wildmarke"] ?? "") } : {})} />
          ))}
        </section>

        <Dialog name="Korrigieren" symbol="korrigieren" offen={offen?.art === "Korrigieren"} onSchliessen={() => setOffen(null)}>
          <Textfeld label="Ansprache" wert={feld("Ansprache")} onChange={setzen("Ansprache")} />
          <Textfeld label="Grund" wert={feld("Grund")} onChange={setzen("Grund")} />
          <div className="knopfreihe">
            <Knopf symbol="haken" onPress={() => void senden("StreckeneintragKorrigieren", { Eintrag: eintragId, Ansprache: feld("Ansprache"), Grund: feld("Grund") })}>Speichern</Knopf>
          </div>
        </Dialog>
        <Dialog name="Entfernen" symbol="schliessen" offen={offen?.art === "Entfernen"} onSchliessen={() => setOffen(null)}>
          <Textfeld label="Grund" wert={feld("Grund")} onChange={setzen("Grund")} />
          <div className="knopfreihe">
            <Knopf symbol="haken" onPress={() => void senden("StreckeneintragEntfernen", { Eintrag: eintragId, Grund: feld("Grund") })}>Speichern</Knopf>
          </div>
        </Dialog>
        <Dialog name="Wildmarke" symbol="wildmarke" offen={offen?.art === "Wildmarke"} onSchliessen={() => setOffen(null)}>
          <Textfeld label="Trichinenprobennummer" wert={feld("Trichinenprobennummer")} onChange={setzen("Trichinenprobennummer")} mono />
          <Textfeld label="Wildmarke" wert={feld("Wildmarke")} onChange={setzen("Wildmarke")} mono />
          <div className="knopfreihe">
            <Knopf symbol="haken" onPress={() => void senden("TrichinenprobennummerUndWildmarkeErfassen", {
              Eintrag: eintragId, Trichinenprobennummer: feld("Trichinenprobennummer"), Wildmarke: feld("Wildmarke"),
            })}>Speichern</Knopf>
          </div>
        </Dialog>
        <Dialog name="Streckeneintrag" symbol="hinzufuegen" offen={offen?.art === "Streckeneintrag"} onSchliessen={() => setOffen(null)}>
          <Textfeld label="Ansprache" wert={feld("Ansprache")} onChange={setzen("Ansprache")} />
          <Klappliste label="Erlegt durch" wert={feld("Erleger") || null} onChange={setzen("Erleger")}
            optionen={mitwirkende.map((m) => ({ wert: m._id, text: wert(m["Name"]) }))} />
          <Textfeld label="Datum" wert={feld("Datum")} onChange={setzen("Datum")} eingabemodus="numeric" mono />
          <Textfeld label="Grund" wert={feld("Grund")} onChange={setzen("Grund")} />
          <div className="knopfreihe">
            <Knopf symbol="haken" onPress={() => void senden("StreckeneintragHinzufuegen", {
              Revier: revier, Ansprache: feld("Ansprache"), Erleger: feld("Erleger"), Datum: feld("Datum"), Grund: feld("Grund"),
            })}>Speichern</Knopf>
          </div>
        </Dialog>
        <Dialog name="Abschließen" symbol="haken" bestaetigung offen={offen?.art === "Abschließen"} onSchliessen={() => setOffen(null)}>
          <div className="knopfreihe">
            <Knopf symbol="haken" onPress={async () => { if (await senden("StreckeAbschliessen", {})) setHinweis("abgeschlossen"); }}>Bestätigen</Knopf>
            <Knopf art="zweit" symbol="schliessen" onPress={() => setOffen(null)}>Abbrechen</Knopf>
          </div>
        </Dialog>
        <Dialog name="Exportieren" symbol="herunterladen" offen={offen?.art === "Exportieren"} onSchliessen={() => setOffen(null)}>
          <Auswahl label="Format" optionen={["PDF", "CSV"]} wert={feld("Format") || "PDF"} onChange={setzen("Format")} />
          <div className="knopfreihe">
            <Knopf symbol="herunterladen" onPress={() => void exportieren()}>Speichern</Knopf>
          </div>
        </Dialog>
        <Dialog name="Rückmeldung" symbol="nachricht" offen={offen?.art === "Rückmeldung"} onSchliessen={() => setOffen(null)}>
          <Auswahl label="Bewertung" optionen={["1", "2", "3", "4", "5"]} wert={feld("Bewertung") || null} onChange={setzen("Bewertung")} />
          <Textfeld label="Was war gut?" wert={feld("Was war gut?")} onChange={setzen("Was war gut?")} />
          <Textfeld label="Was verbessern?" wert={feld("Was verbessern?")} onChange={setzen("Was verbessern?")} />
          <Ankreuzfeld label="anonym" an={anonym} onChange={setAnonym} />
          <div className="knopfreihe">
            <Knopf symbol="senden" onPress={async () => {
              if (await senden("RueckmeldungAbgeben", {
                Bewertung: feld("Bewertung") ? Number(feld("Bewertung")) : undefined, "Was war gut?": feld("Was war gut?"),
                "Was verbessern?": feld("Was verbessern?"), anonym,
              })) setHinweis("Rückmeldung");
            }}>Senden</Knopf>
          </div>
        </Dialog>
      </>
    ),
  });
}
